// PWA service-worker registration and detection helpers.
//
// The project's single service worker (`/push-sw.js`) handles installability
// (so the app can be added to the Home Screen as a standalone PWA) and web-push
// messaging. Registering it early — on every page load — keeps the PWA install
// quality high and makes the worker ready before the admin subscribes to push.

export const MANIFEST_PATH = "/manifest.webmanifest";
const SW_PATH = "/push-sw.js";

function canRegister(): boolean {
  try {
    if (typeof window === "undefined") return false;
    if (!("serviceWorker" in navigator)) return false;
    if (!window.isSecureContext) return false;
    if (window.self !== window.top) return false; // inside an iframe

    const host = window.location.hostname;
    if (host.startsWith("id-preview--") || host.startsWith("preview--")) return false;

    return true;
  } catch {
    return false;
  }
}

export function registerServiceWorker(): void {
  if (!canRegister()) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register(SW_PATH).catch(() => {
      /* best-effort: ignore service-worker registration failures */
    });
  });
}

/** True on iOS / iPadOS Safari (covers iPad reporting as Mac with touch). */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  return iOSDevice || iPadOS;
}

/** True when the app runs as an installed PWA (home-screen / standalone). */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari exposes this non-standard flag in home-screen mode.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}
